// src/context/CurrencyContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    try {
      setLoading(true);
      const data = await apiService.getTenantCurrencies();
      setCurrencies(data);

      // Set USD as default, or base currency if found, or first one
      const usdCurrency = data.find(c => c.code === 'USD');
      const baseCurrency = data.find(c => c.isBaseCurrency);
      setSelectedCurrency(usdCurrency || baseCurrency || data[0]);
    } catch (error) {
      console.error('Failed to load currencies:', error);
      // Fallback to EUR if API fails
      setCurrencies([
        { code: 'USD', name: 'US Dollar', symbol: '$', minorUnit: 2, isBaseCurrency: true }
      ]);
      setSelectedCurrency({ code: 'USD', name: 'US Dollar', symbol: '$', minorUnit: 2, isBaseCurrency: true });
    } finally {
      setLoading(false);
    }
  };

  const changeCurrency = (currencyCode) => {
    const currency = currencies.find(c => c.code === currencyCode);
    if (currency) {
      setSelectedCurrency(currency);
      // Store in localStorage for persistence
      localStorage.setItem('selectedCurrency', currencyCode);
    }
  };

  useEffect(() => {
    window.setCurrencyAfterLogin = (currencyCode) => {
      changeCurrency(currencyCode);
    };

    return () => {
      delete window.setCurrencyAfterLogin;
    };
  }, [changeCurrency]);

  const formatPrice = (amount, currencyCode = null) => {
    const currency = currencyCode
      ? currencies.find(c => c.code === currencyCode) || selectedCurrency
      : selectedCurrency;

    if (!currency) return `${amount}`;

    const formattedAmount = parseFloat(amount).toFixed(currency.minorUnit);
    return `${currency.symbol}${formattedAmount}`;
  };

  return (
    <CurrencyContext.Provider value={{
      currencies,
      selectedCurrency,
      loading,
      changeCurrency,
      formatPrice
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;